import { Component, computed, Signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { EmployeeRequest, EmployeeRoleType } from '../../../../../_interfaces/employee.interface';
import { NgClass } from '@angular/common';
import { FieldPathDirective, FormNonFieldErrorsComponent, FormOptionsDirective } from '@bo/ng-forms';
import { FieldComponent } from '../../../../../shared/components/forms/form-field/form-field.component';
import { SubmitComponent } from '../../../../../shared/components/forms/submit/submit.component';
import { PhoneFieldComponent } from '../../../../../shared/components/forms/phone-field/phone-field.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { FieldUnverifiedTextComponent } from '../../../../../shared/components/forms/field-unverified-text/field-unverified-text.component';
import { IDFieldComponent } from '../../../../../shared/components/forms/id-field/id-field.component';
import { AvatarComponent } from '../../../../../shared/components/avatar/avatar.component';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { SelectFieldComponent } from '../../../../../shared/components/forms/select-field/select-field.component';
import { OffcanvasCancelBtnComponent } from '../../../../../shared/components/offcanvas/offcanvas-cancel-btn/offcanvas-cancel-btn.component';
import { EmployeeFormBaseComponent } from '../../add-employee/employee-form-base/employee-form-base.component';
import { Method } from '@bo/ng-api';
import { ReactiveFormsModule } from '@angular/forms';
import { SelectTopupFrequencyComponent } from '../../../../../shared/components/forms/select-topup-frequency/select-topup-frequency.component';
import { CardLimitDisplayComponent } from '../../../../../shared/components/cards/card-limit-display/card-limit-display.component';
import { ToggleSwitchComponent } from '../../../../../shared/components/forms/toggle-switch/toggle-switch.component';

@Component({
  selector: 'app-invited-employee-form',
  imports: [
    AvatarComponent,
    FieldComponent,
    FieldPathDirective,
    FieldUnverifiedTextComponent,
    FormNonFieldErrorsComponent,
    IDFieldComponent,
    LoaderComponent,
    NgbTooltip,
    OffcanvasCancelBtnComponent,
    PhoneFieldComponent,
    ReactiveFormsModule,
    SelectFieldComponent,
    SubmitComponent,
    NgClass,
    FormOptionsDirective,
    SelectTopupFrequencyComponent,
    CardLimitDisplayComponent,
    ToggleSwitchComponent,
  ],
  templateUrl: '../../add-employee/employee-form-base/employee-form-base.component.html',
})
export class InvitedEmployeeFormComponent extends EmployeeFormBaseComponent {
  protected override returnUrl: Signal<string> = computed(() => {
    return '/employee-invitations';
  });

  protected override existingEmployee = computed(() => false);

  override createForm(): void {
    const options$ = this.employeeApiService.getOptions(this.employeeApiService.URL_EMPLOYEE_SIGNUP + '/', Method.POST);
    const data$ = this.getInitialData();

    this.subscriptions.add(
      this.buildFromOptions(options$, data$).subscribe({
        next: () => {
          this.handleRoleValueChanges();
          this.handleCreateCardToggleChanges();
          this.handleTopUpAmountChanges();
          this.handleInitialDataSubscription(data$);

          // Don't show or edit this field after the employee is created.
          // It's only used for initial sign-up validation and KYC
          if (this.invitationData()) {
            this.form.get('date_of_birth')?.disable();
          }

          this.subscriptions.add(
            this.form.statusChanges.subscribe(() => {
              this.isFormInvalid.set(this.form.invalid);
            }),
          );
          // Initial set
          this.isFormInvalid.set(this.form.invalid);
        },
      }),
    );
  }

  private handleInitialDataSubscription(data$: Observable<EmployeeRequest>): void {
    this.subscriptions.add(
      data$.subscribe((data) => {
        // set initial fields
        if (this.invitationData()?.create_card) {
          this.showApproverFields.set(true);
          this.showRecurringSwitch.set(true);
        }

        if (this.invitationData()?.role === EmployeeRoleType.USER) {
          this.forceCreateCard();
        } else {
          this.setCardFieldsBasedOnCreateCard();
        }

        if (this.invitationData()?.topup_frequency) {
          this.showRecurringTopUp.set(true);
          this.setRecurringFields();
        }
        this.patchFormData(data);
      }),
    );
  }

  private getInitialData() {
    return this.employeeApiService.getInvitedEmployeeById(this.employeeId()).pipe(
      tap((invitation: EmployeeRequest) => {
        this.invitationData.set(invitation);
        this.ruleFrequency = invitation.topup_frequency;
        return invitation;
      }),
    );
  }

  protected override toggleCreateCard(): void {
    this.form.get('create_card')?.enable();
    this.handleCreateCardToggleChanges();
  }

  private patchFormData(data: EmployeeRequest): void {
    this.form.get('phone')?.patchValue(data.phone?.replace('+27', ''));
    this.form.get('topup_amount')?.patchValue(data.topup_amount);
    if (data.topup_frequency) this.handleFrequencyChange(data.topup_frequency);
  }

  override submitData(formData: EmployeeRequest): Observable<EmployeeRequest> {
    const data = formData;
    if (this.showRecurringTopUp()) {
      data.topup_frequency = this.selectedFrequency();
    } else {
      data.topup_frequency = null;
      data.topup_amount = null;
    }
    return this.employeeApiService.updateInvitedEmployee(this.employeeId(), data);
  }

  override handleSubmitSuccess(): void {
    this.offcanvasService.close();
    this.toastService.show('Employee invitation updated successfully.', 'iconoir-check-circle');
    this.offcanvasService.offCanvasNavigate(`/employee-invitations`);
  }
}
